import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession, AmountInput } from 'gunsi-auth'
import { BottomSheet } from '../components/BottomSheet.jsx'
import { Select } from '../components/Select.jsx'
import { CurrencySelect } from '../components/CurrencySelect.jsx'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { youOf, makeMemberLabel } from '../util/you.js'
import { usedCurrencies, lastUsedCurrency } from '../util/balances.js'
import { parseAmount } from '../util/amount.js'

function todayISODate() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function SettleFormSheet({ open, onClose, group, settlement }) {
  const t = useEvenI18n()
  const { session } = useSession()
  const { confirm } = useConfirm()
  const {
    addSettlement, updateSettlement, removeSettlement,
    expenses: allExpenses, settlements: allSettlements, currency: userCurrency,
  } = useEven()
  const isEdit = Boolean(settlement)
  const you = youOf(group, session?.user?.id)

  const [from, setFrom]         = useState('')
  const [to, setTo]             = useState('')
  const [amountStr, setAmountStr] = useState('')
  const [date, setDate]         = useState(todayISODate())
  const [notes, setNotes]       = useState('')
  const [cur, setCur]           = useState('EUR')

  const members = group?.members ?? []
  const memberLabel = makeMemberLabel(group)
  const recentCurrencies = useMemo(() => {
    if (!group) return []
    const gExp = allExpenses.filter(e => e.groupId === group.id)
    const gSet = allSettlements.filter(s => s.groupId === group.id)
    return [...new Set([...usedCurrencies(group, gExp, gSet), userCurrency].filter(Boolean))]
  }, [group, allExpenses, allSettlements, userCurrency])

  // Dipendenze sugli ID e non sugli oggetti: `refresh()` su focus rimpiazza
  // groups/settlements con istanze nuove e resetterebbe il form a metà
  useEffect(() => {
    if (!open) return
    if (settlement) {
      setFrom(settlement.from)
      setTo(settlement.to)
      setAmountStr(String(settlement.amount ?? ''))
      setDate(settlement.date?.slice(0, 10) || todayISODate())
      setNotes(settlement.notes ?? '')
      setCur(settlement.currency || group?.currency || 'EUR')
    } else {
      const other = members.find(m => !you || m.id !== you.id)
      setFrom(you?.id || members[0]?.id || '')
      setTo(other?.id || members[1]?.id || '')
      setAmountStr('')
      setDate(todayISODate())
      setNotes('')
      setCur(lastUsedCurrency(group, allExpenses, userCurrency))
    }
    // Il componente resta montato a sheet chiuso: senza questo il blocco
    // anti-doppio-invio sopravvive al salvataggio e riapre il form col
    // pulsante disabilitato
    savingRef.current = false
    setSaving(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settlement?.id, group?.id])

  // Come in ExpenseForm: due tap veloci creavano due pagamenti. La ref vale
  // già nel secondo evento, prima che il re-render disabiliti il pulsante.
  const savingRef = useRef(false)
  const [saving, setSaving] = useState(false)

  async function onSubmit(e) {
    e.preventDefault()
    if (savingRef.current) return
    const amount = parseAmount(amountStr) || 0
    if (!amount || !from || !to || from === to) return
    const payload = {
      groupId: group.id,
      from, to, amount, currency: cur, date, notes: notes.trim(),
    }
    savingRef.current = true
    setSaving(true)
    try {
      if (isEdit) await updateSettlement(settlement.id, payload)
      else        await addSettlement(payload)
    } catch (err) {
      savingRef.current = false
      setSaving(false)
      throw err
    }
    onClose()
  }

  async function onDelete() {
    if (!settlement) return
    const ok = await confirm({
      message: t.settle.deleteConfirm,
      okLabel: t.common.delete,
      danger: true,
    })
    if (!ok) return
    await removeSettlement(settlement.id)
    onClose()
  }

  if (!group) return null

  return (
    <BottomSheet open={open} onClose={onClose} className="bsheet-form">
      <form onSubmit={onSubmit} className="form-sheet">
        <header className="form-sheet-head">
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t.settle.cancel}</button>
          <h2>{t.settle.title}</h2>
          <button type="submit" className="btn btn-primary" disabled={saving}>{t.settle.save}</button>
        </header>

        <div className="form-sheet-body">
        <div className="form-row">
          <label>{t.settle.from}</label>
          <Select
            value={from}
            onChange={setFrom}
            options={members.map(m => ({ value: m.id, label: memberLabel(m.id) }))}
            label={t.settle.from}
          />
        </div>

        <div className="form-row">
          <label>{t.settle.to}</label>
          <Select
            value={to}
            onChange={setTo}
            options={members.map(m => ({ value: m.id, label: memberLabel(m.id) }))}
            label={t.settle.to}
          />
        </div>

        <div className="form-row">
          <label>{t.settle.amount}</label>
          <div className="amount-input-row">
            <AmountInput
              value={amountStr}
              onChange={setAmountStr}
              placeholder="0.00"
              autoFocus
            />
            <CurrencySelect
              value={cur}
              onChange={setCur}
              recentCodes={recentCurrencies}
              title={t.common.currency}
              searchPlaceholder={t.common.searchCurrencyPh}
            />
          </div>
        </div>

        <div className="form-row">
          <label>{t.settle.date}</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>

        <div className="form-row">
          <label>{t.settle.notes}</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
          />
        </div>

        {isEdit && (
          <div className="form-row">
            <button type="button" className="btn btn-danger" onClick={onDelete}>{t.common.delete}</button>
          </div>
        )}
        </div>
      </form>
    </BottomSheet>
  )
}
